import { ReactSimpleComponentProps } from '@/custom-types';
import { useCallback, useMemo, useReducer, useState } from 'react';
import {
  CreateCommunicationContext,
  CreateCommunicationContextActions,
  CreateCommunicationState,
  CommunicationAudienceOption,
  CommunicationMode,
  PropertySelectionMode,
  UploadedCommunicationFile,
  CreateCommunicationAction,
} from './CreateCommunicationContext';

const emptyState: CreateCommunicationState = {
  title: '',
  message: '',
  targetUserTypes: [],
  uploadedFiles: [],
  selectedTemplateId: null,
  mode: 'TEMPLATES',
  propertySelectionMode: 'ALL',
  selectedPropertyIds: [],
  allStakeholdersOnSelectedProperties: false,
  selectedUserIds: [],
  allUsersSelected: true,
  onlyActiveContractsSelected: false,
  excludeRecipientUserIds: [],
};

const createCommunicationReducer = (
  state: CreateCommunicationState,
  action: CreateCommunicationAction,
): CreateCommunicationState => {
  switch (action.type) {
    case 'CREATE_COMMUNICATION-setTitle':
      return { ...state, title: action.payload.title };
    case 'CREATE_COMMUNICATION-setMessage':
      return { ...state, message: action.payload.message };
    case 'CREATE_COMMUNICATION-toggleAudience': {
      const exists = state.targetUserTypes.includes(action.payload.option);
      return {
        ...state,
        targetUserTypes: exists
          ? state.targetUserTypes.filter((o) => o !== action.payload.option)
          : [...state.targetUserTypes, action.payload.option],
      };
    }
    case 'CREATE_COMMUNICATION-setTargetUserTypes':
      return { ...state, targetUserTypes: action.payload.types };
    case 'CREATE_COMMUNICATION-setMode':
      return { ...state, mode: action.payload.mode };
    case 'CREATE_COMMUNICATION-setSelectedTemplate':
      return { ...state, selectedTemplateId: action.payload.id };
    case 'CREATE_COMMUNICATION-addUploadedFiles':
      if (!action.payload.files.length) return state;
      return {
        ...state,
        uploadedFiles: [...state.uploadedFiles, ...action.payload.files],
      };
    case 'CREATE_COMMUNICATION-clearUploadedFiles':
      return { ...state, uploadedFiles: [] };
    case 'CREATE_COMMUNICATION-setPropertySelectionMode':
      return { ...state, propertySelectionMode: action.payload.mode };
    case 'CREATE_COMMUNICATION-setSelectedPropertyIds':
      return { ...state, selectedPropertyIds: action.payload.ids };
    case 'CREATE_COMMUNICATION-setSelectedUserIds':
      return { ...state, selectedUserIds: action.payload.ids };
    case 'CREATE_COMMUNICATION-toggleUserSelection': {
      const exists = state.selectedUserIds.includes(action.payload.userId);
      return {
        ...state,
        selectedUserIds: exists
          ? state.selectedUserIds.filter((id) => id !== action.payload.userId)
          : [...state.selectedUserIds, action.payload.userId],
      };
    }
    case 'CREATE_COMMUNICATION-setAllUsersSelected':
      return { ...state, allUsersSelected: action.payload.value };
    case 'CREATE_COMMUNICATION-setOnlyActiveContractsSelected':
      return {
        ...state,
        onlyActiveContractsSelected: action.payload.value,
      };
    case 'CREATE_COMMUNICATION-setAllStakeholdersOnSelectedProperties':
      return {
        ...state,
        allStakeholdersOnSelectedProperties: action.payload.value,
      };
    case 'CREATE_COMMUNICATION-toggleExcludeRecipientUser': {
      const exists = state.excludeRecipientUserIds.includes(
        action.payload.userId,
      );
      return {
        ...state,
        excludeRecipientUserIds: exists
          ? state.excludeRecipientUserIds.filter(
              (id) => id !== action.payload.userId,
            )
          : [...state.excludeRecipientUserIds, action.payload.userId],
      };
    }
    case 'CREATE_COMMUNICATION-setExcludeRecipientUserIds':
      return { ...state, excludeRecipientUserIds: action.payload.ids };
    default:
      return state;
  }
};

export const CreateCommunicationsContextProvider = ({
  children,
}: ReactSimpleComponentProps) => {
  const [state, dispatch] = useReducer(createCommunicationReducer, emptyState);
  const [isDisabled, setIsDisabled] = useState(true);

  const setTitle = useCallback((title: string) => {
    dispatch({ type: 'CREATE_COMMUNICATION-setTitle', payload: { title } });
  }, []);
  const setMessage = useCallback((message: string) => {
    dispatch({ type: 'CREATE_COMMUNICATION-setMessage', payload: { message } });
  }, []);
  const toggleAudience = useCallback((option: CommunicationAudienceOption) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-toggleAudience',
      payload: { option },
    });
  }, []);
  const setTargetUserTypes = useCallback((types: CommunicationAudienceOption[]) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setTargetUserTypes',
      payload: { types },
    });
  }, []);
  const setMode = useCallback((mode: CommunicationMode) => {
    dispatch({ type: 'CREATE_COMMUNICATION-setMode', payload: { mode } });
  }, []);
  const setSelectedTemplate = useCallback((id: string | null) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setSelectedTemplate',
      payload: { id },
    });
  }, []);
  const addUploadedFiles = useCallback((files: UploadedCommunicationFile[]) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-addUploadedFiles',
      payload: { files },
    });
  }, []);
  const clearUploadedFiles = useCallback(() => {
    dispatch({ type: 'CREATE_COMMUNICATION-clearUploadedFiles' });
  }, []);
  const setPropertySelectionMode = useCallback((mode: PropertySelectionMode) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setPropertySelectionMode',
      payload: { mode },
    });
  }, []);
  const setSelectedPropertyIds = useCallback((ids: string[]) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setSelectedPropertyIds',
      payload: { ids },
    });
  }, []);
  const setSelectedUserIds = useCallback((ids: string[]) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setSelectedUserIds',
      payload: { ids },
    });
  }, []);
  const toggleUserSelection = useCallback((userId: string) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-toggleUserSelection',
      payload: { userId },
    });
  }, []);
  const setAllUsersSelected = useCallback((value: boolean) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setAllUsersSelected',
      payload: { value },
    });
  }, []);
  const setOnlyActiveContractsSelected = useCallback((value: boolean) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setOnlyActiveContractsSelected',
      payload: { value },
    });
  }, []);
  const setAllStakeholdersOnSelectedProperties = useCallback((value: boolean) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setAllStakeholdersOnSelectedProperties',
      payload: { value },
    });
  }, []);
  const toggleExcludeRecipientUser = useCallback((userId: string) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-toggleExcludeRecipientUser',
      payload: { userId },
    });
  }, []);
  const setExcludeRecipientUserIds = useCallback((ids: string[]) => {
    dispatch({
      type: 'CREATE_COMMUNICATION-setExcludeRecipientUserIds',
      payload: { ids },
    });
  }, []);
  const setButtonDisabled = useCallback(() => setIsDisabled(true), []);
  const setButtonEnabled = useCallback(() => setIsDisabled(false), []);

  const contextValue = useMemo<CreateCommunicationContextActions>(
    () => ({
      state,
      setTitle,
      setMessage,
      toggleAudience,
      setTargetUserTypes,
      setMode,
      setSelectedTemplate,
      addUploadedFiles,
      clearUploadedFiles,
      setPropertySelectionMode,
      setSelectedPropertyIds,
      setSelectedUserIds,
      toggleUserSelection,
      setAllUsersSelected,
      setOnlyActiveContractsSelected,
      setAllStakeholdersOnSelectedProperties,
      toggleExcludeRecipientUser,
      setExcludeRecipientUserIds,
      buttonState: isDisabled,
      setButtonDisabled,
      setButtonEnabled,
    }),
    [
      state,
      isDisabled,
      setTitle,
      setMessage,
      toggleAudience,
      setTargetUserTypes,
      setMode,
      setSelectedTemplate,
      addUploadedFiles,
      clearUploadedFiles,
      setPropertySelectionMode,
      setSelectedPropertyIds,
      setSelectedUserIds,
      toggleUserSelection,
      setAllUsersSelected,
      setOnlyActiveContractsSelected,
      setAllStakeholdersOnSelectedProperties,
      toggleExcludeRecipientUser,
      setExcludeRecipientUserIds,
      setButtonDisabled,
      setButtonEnabled,
    ],
  );

  return (
    <CreateCommunicationContext.Provider value={contextValue}>
      {children}
    </CreateCommunicationContext.Provider>
  );
};

